import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { ChevronRight, MapPin, Building2, Users2, ArrowLeft, Search } from "lucide-react";

/**
 * Territories page — hierarchical drill-down browser.
 * Level 1: States (grid of tiles with counts of districts / distributors / parties).
 * Level 2: Districts within the selected state (grid of tiles per district).
 * Level 3: All distributors + parties within the selected (state, district).
 *
 * Territories are auto-created on distributor / party creation, so this page
 * needs no manual "Add Territory" flow anymore — it becomes a pure browser.
 */
export default function Territories() {
  const [tree, setTree] = useState({ states: [] });
  const [search, setSearch] = useState("");
  const [selectedState, setSelectedState] = useState(null); // string
  const [selectedDistrict, setSelectedDistrict] = useState(null); // string
  const [drill, setDrill] = useState(null); // { distributors, parties }
  const [drillLoading, setDrillLoading] = useState(false);

  const loadTree = useCallback(async () => {
    try {
      const { data } = await api.get("/distribution/territories/tree");
      setTree(data || { states: [] });
    } catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { loadTree(); }, [loadTree]);

  const goBack = () => {
    if (selectedDistrict) {
      setSelectedDistrict(null);
      setDrill(null);
    } else if (selectedState) {
      setSelectedState(null);
    }
  };

  const enterState = (state) => {
    setSelectedState(state);
    setSelectedDistrict(null);
    setSearch("");
  };

  const enterDistrict = async (district) => {
    setSelectedDistrict(district);
    setSearch("");
    setDrillLoading(true);
    try {
      const q = new URLSearchParams({ state: selectedState });
      if (district && district !== "(Unspecified)") q.append("district", district);
      const { data } = await api.get(`/distribution/territories/drilldown?${q.toString()}`);
      setDrill(data);
    } catch (e) {
      toast.error(formatApiError(e));
      setDrill({ distributors: [], parties: [] });
    } finally {
      setDrillLoading(false);
    }
  };

  const stateNode = useMemo(
    () => selectedState ? tree.states.find((s) => s.state === selectedState) : null,
    [tree, selectedState],
  );

  // -------- Level 3: District view (distributors + parties) --------
  if (selectedState && selectedDistrict) {
    const q = search.trim().toLowerCase();
    const filteredDist = (drill?.distributors || []).filter((d) =>
      !q || d.distributor_name?.toLowerCase().includes(q) || (d.city || "").toLowerCase().includes(q),
    );
    const filteredParties = (drill?.parties || []).filter((p) =>
      !q || p.party_name?.toLowerCase().includes(q) || (p.city || "").toLowerCase().includes(q),
    );
    return (
      <div className="space-y-6" data-testid="territory-drilldown">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <button onClick={() => { setSelectedState(null); setSelectedDistrict(null); setDrill(null); }} className="hover:underline">Territories</button>
          <ChevronRight className="w-3 h-3" />
          <button onClick={() => { setSelectedDistrict(null); setDrill(null); }} className="hover:underline">{selectedState}</button>
          <ChevronRight className="w-3 h-3" />
          <span className="font-medium text-foreground">{selectedDistrict}</span>
        </div>

        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-display font-bold text-2xl tracking-tight">
              {selectedDistrict}, {selectedState}
            </h1>
            <p className="text-sm text-muted-foreground">
              {(drill?.distributors || []).length} distributor{(drill?.distributors || []).length === 1 ? "" : "s"} · {(drill?.parties || []).length} part{(drill?.parties || []).length === 1 ? "y" : "ies"}
            </p>
          </div>
          <Button variant="outline" onClick={goBack} data-testid="territory-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to {selectedState}
          </Button>
        </div>

        <div className="relative max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search within this district..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="territory-search"
          />
        </div>

        {drillLoading ? (
          <div className="text-sm text-muted-foreground">Loading...</div>
        ) : (
          <div className="space-y-6">
            <Card className="overflow-hidden">
              <div className="px-4 py-3 border-b flex items-center gap-2 bg-muted/30">
                <Building2 className="w-4 h-4 text-blue-600" />
                <div className="font-medium">Distributors ({filteredDist.length})</div>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Code</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>City</TableHead>
                      <TableHead>Mobile</TableHead>
                      <TableHead>Registration</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredDist.length === 0 && (
                      <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">No distributors here yet.</TableCell></TableRow>
                    )}
                    {filteredDist.map((d) => (
                      <TableRow key={d.id} data-testid={`territory-dist-${d.id}`}>
                        <TableCell className="font-mono text-xs">{d.distributor_code || "—"}</TableCell>
                        <TableCell className="font-medium">{d.distributor_name}</TableCell>
                        <TableCell>{d.city || "—"}</TableCell>
                        <TableCell className="text-xs">{d.mobile || "—"}</TableCell>
                        <TableCell><span className={`inline-block px-2 py-0.5 text-[10px] rounded ${d.registration_status === "Registered" ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`}>{d.registration_status || "Pending"}</span></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Card>

            <Card className="overflow-hidden">
              <div className="px-4 py-3 border-b flex items-center gap-2 bg-muted/30">
                <Users2 className="w-4 h-4 text-purple-600" />
                <div className="font-medium">Parties ({filteredParties.length})</div>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Code</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>City</TableHead>
                      <TableHead>Mgmt Type</TableHead>
                      <TableHead>Registration</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredParties.length === 0 && (
                      <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">No parties here yet.</TableCell></TableRow>
                    )}
                    {filteredParties.map((p) => (
                      <TableRow key={p.id} data-testid={`territory-party-${p.id}`}>
                        <TableCell className="font-mono text-xs">{p.party_code || "—"}</TableCell>
                        <TableCell className="font-medium">{p.party_name}</TableCell>
                        <TableCell>{p.city || "—"}</TableCell>
                        <TableCell className="text-xs">{p.management_type || "—"}</TableCell>
                        <TableCell><span className={`inline-block px-2 py-0.5 text-[10px] rounded ${p.registration_status === "Registered" ? "bg-green-100 text-green-800" : "bg-amber-100 text-amber-800"}`}>{p.registration_status || "Not Registered"}</span></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </Card>
          </div>
        )}
      </div>
    );
  }

  // -------- Level 2: District tiles for the selected state --------
  if (selectedState && stateNode) {
    const q = search.trim().toLowerCase();
    const filtered = stateNode.districts.filter((d) => !q || d.district.toLowerCase().includes(q));
    return (
      <div className="space-y-6" data-testid="territory-district-list">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <button onClick={() => setSelectedState(null)} className="hover:underline">Territories</button>
          <ChevronRight className="w-3 h-3" />
          <span className="font-medium text-foreground">{selectedState}</span>
        </div>
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-display font-bold text-2xl tracking-tight">{selectedState}</h1>
            <p className="text-sm text-muted-foreground">
              {stateNode.districts.length} district{stateNode.districts.length === 1 ? "" : "s"} · {stateNode.distributors} distributor{stateNode.distributors === 1 ? "" : "s"} · {stateNode.parties} part{stateNode.parties === 1 ? "y" : "ies"}
            </p>
          </div>
          <Button variant="outline" onClick={goBack} data-testid="territory-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back to States
          </Button>
        </div>

        <div className="relative max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Filter districts..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="territory-search"
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.length === 0 && (
            <div className="text-sm text-muted-foreground col-span-full">No districts match.</div>
          )}
          {filtered.map((d) => (
            <button
              key={d.district}
              onClick={() => enterDistrict(d.district)}
              className="text-left p-4 rounded-lg border border-border hover:border-primary hover:shadow-md transition-all bg-white group"
              data-testid={`territory-district-tile-${d.district}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate group-hover:text-primary">{d.district}</div>
                  <div className="text-xs text-muted-foreground mt-1">
                    {d.cities.length} cit{d.cities.length === 1 ? "y" : "ies"}
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
              </div>
              <div className="flex gap-3 mt-3 text-xs">
                <span className="inline-flex items-center gap-1 text-blue-700">
                  <Building2 className="w-3 h-3" /> {d.distributors}
                </span>
                <span className="inline-flex items-center gap-1 text-purple-700">
                  <Users2 className="w-3 h-3" /> {d.parties}
                </span>
              </div>
            </button>
          ))}
        </div>
      </div>
    );
  }

  // -------- Level 1: State tiles --------
  const q = search.trim().toLowerCase();
  const filteredStates = tree.states.filter((s) => !q || s.state.toLowerCase().includes(q));

  return (
    <div className="space-y-6" data-testid="territory-state-list">
      <div>
        <h1 className="font-display font-bold text-2xl tracking-tight flex items-center gap-2">
          <MapPin className="w-6 h-6 text-primary" /> Territories
        </h1>
        <p className="text-sm text-muted-foreground">
          Auto-updated as distributors and parties are added. Click a state to drill down.
        </p>
      </div>

      <div className="relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input
          placeholder="Search state..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="pl-9"
          data-testid="territory-search"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {filteredStates.length === 0 && (
          <div className="text-sm text-muted-foreground col-span-full py-8 text-center border border-dashed rounded-lg">
            {tree.states.length === 0
              ? "No territories yet. They appear here automatically as you add distributors or parties."
              : "No states match."}
          </div>
        )}
        {filteredStates.map((s) => (
          <button
            key={s.state}
            onClick={() => enterState(s.state)}
            className="text-left p-4 rounded-lg border border-border hover:border-primary hover:shadow-md transition-all bg-white group"
            data-testid={`territory-state-tile-${s.state}`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0 flex-1">
                <div className="font-medium truncate group-hover:text-primary">{s.state}</div>
                <div className="text-xs text-muted-foreground mt-1">
                  {s.districts.length} district{s.districts.length === 1 ? "" : "s"} · {s.cities} cit{s.cities === 1 ? "y" : "ies"}
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
            </div>
            <div className="flex gap-3 mt-3 text-xs">
              <span className="inline-flex items-center gap-1 text-blue-700">
                <Building2 className="w-3 h-3" /> {s.distributors}
              </span>
              <span className="inline-flex items-center gap-1 text-purple-700">
                <Users2 className="w-3 h-3" /> {s.parties}
              </span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
